import type { ReactNode } from 'react';

import { cva, type VariantProps } from 'class-variance-authority';
import { ArrowLeft } from 'lucide-react';

import { useBreakpointDown } from '@nesso/ui/hooks/use-breakpoint-down';
import { cn } from '@nesso/ui/lib/utils';
import { Alert, AlertDescription, AlertTitle } from '@nesso/ui/primitives/alert';
import { Button } from '@nesso/ui/primitives/button';
import {
    Card,
    CardContent,
    CardDescription,
    CardFooter,
    CardHeader,
    CardTitle,
} from '@nesso/ui/primitives/card';

import { ProgressBar } from '@/shared/ui/indicators/progress-bar';

const stepCardVariants = cva(
    'w-full mx-auto pb-0 flex flex-col gap-0 justify-between relative overflow-hidden',
    {
        variants: {
            variant: {
                pinned: 'pt-14 md:pt-12 sm:w-128 md:w-167 lg:w-206 xl:w-260 sm:mt-12 flex-1 rounded-none sm:rounded-t-sm sm:border-b-0',
                centered:
                    'bg-transparent border-0 rounded-none mt-12 sm:mt-8.5 sm:bg-card sm:border-border sm:rounded-sm sm:border h-full sm:h-auto w-[calc(100%-2rem)] md:w-162.5 pt-0 md:pt-0',
            },
        },
        defaultVariants: {
            variant: 'pinned',
        },
    },
);

const stepCardContentVariants = cva('overflow-y-auto', {
    variants: {
        variant: {
            pinned: 'flex-1 px-4 md:px-20',
            centered:
                'px-4 py-10 sm:pt-10 sm:pb-0 bg-card border-border rounded-sm border sm:bg-transparent sm:border-none sm:rounded-none',
        },
    },
    defaultVariants: {
        variant: 'pinned',
    },
});

const stepCardHeaderVariants = cva('max-w-197.5 mb-6', {
    variants: {
        variant: {
            pinned: 'px-4 md:px-20',
            centered: 'px-0 sm:px-20 text-center',
        },
    },
    defaultVariants: {
        variant: 'pinned',
    },
});

const stepCardFooterVariants = cva('flex justify-end gap-3', {
    variants: {
        variant: {
            pinned: 'p-4',
            centered: 'px-0 py-4 sm:px-4',
        },
    },
    defaultVariants: {
        variant: 'pinned',
    },
});

type Props = {
    children: ReactNode;
    progressValue?: number;
    title: string | ReactNode;
    titleIcon?: ReactNode;
    description?: string;
    className?: string;
    cardTitleClassName?: string;
    cardContentClassName?: string;
    isError?: boolean;
    errorMessage?: string;
    isFatalError?: boolean;
    showBackButton?: boolean;
    showNextButton?: boolean;
    disabledNextButton?: boolean;
    disabledBackButton?: boolean;
    onNext?: () => void;
    onBack?: () => void;
} & VariantProps<typeof stepCardVariants>;

export function CardFormSection({
    variant = 'pinned',
    children,
    progressValue,
    titleIcon,
    title,
    description,
    errorMessage,
    isError = false,
    isFatalError = false,
    showBackButton = true,
    showNextButton = true,
    disabledNextButton = false,
    disabledBackButton = false,
    className,
    cardTitleClassName,
    cardContentClassName,
    onNext,
    onBack,
}: Props) {
    const isSm = useBreakpointDown('sm');
    const isCentered = variant === 'centered';
    const hasShowFooter = showBackButton || showNextButton;

    const renderHeader = () => {
        return (
            <CardHeader className={cn(stepCardHeaderVariants({ variant }))}>
                {isFatalError && (
                    <Alert variant="destructive" className="mb-6">
                        <AlertTitle>Unable to save your response</AlertTitle>
                        <AlertDescription>
                            Check your internet connection and try again
                        </AlertDescription>
                    </Alert>
                )}
                {titleIcon && <div className="flex justify-center mb-4 sm:mb-8">{titleIcon}</div>}
                <CardTitle className={cn('text-3xl sm:text-4xl', cardTitleClassName)}>
                    {title}
                </CardTitle>
                {description && (
                    <CardDescription className={cn(isError && 'text-destructive')}>
                        {description}
                    </CardDescription>
                )}
                {errorMessage && (
                    <CardDescription className="text-destructive">{errorMessage}</CardDescription>
                )}
            </CardHeader>
        );
    };

    return (
        <Card className={cn(stepCardVariants({ variant }), className)}>
            {!isCentered && renderHeader()}
            <CardContent className={cn(stepCardContentVariants({ variant }), cardContentClassName)}>
                {!isCentered && progressValue !== undefined && (
                    <div className="absolute top-0 left-0 w-full">
                        <ProgressBar value={progressValue} />
                    </div>
                )}
                {isCentered && renderHeader()}
                {children}
            </CardContent>
            {hasShowFooter && (
                <CardFooter className={cn(stepCardFooterVariants({ variant }))}>
                    {showBackButton && (
                        <Button
                            variant="outline"
                            size={isSm ? 'icon' : 'lg'}
                            className="h-10 text-base px-2.25 sm:px-5.5"
                            onClick={onBack}
                            disabled={disabledBackButton}
                        >
                            {isSm ? <ArrowLeft size={16} /> : 'Back'}
                        </Button>
                    )}
                    {showNextButton && (
                        <Button
                            disabled={disabledNextButton}
                            onClick={onNext}
                            className="h-10 flex-1 sm:flex-none text-base px-5.5"
                        >
                            Next
                        </Button>
                    )}
                </CardFooter>
            )}
        </Card>
    );
}
